// The GPU side of the field cache: one resident texture per field filter + field KEY (DECISIONS U2b), shared by every shader node that draws it and by the field warm (DECISIONS 2026-10-07 plan: fields warm before a fade). A node holds a reference per key; the warm pins its set. A texture with no holder and no pin is destroyed, or handed to a new key of the same size by the node that let it go.
import { BufferImageSource } from 'pixi.js'
import { FIELD_CACHE_BYTES } from '~/lib/surfaceField/cache'

const logger = console.scope('fieldTextures')

// How a field is filtered: 'linear' when the stage renderer filters 32F textures (Pixi enables OES_texture_float_linear at context init, U4a), else 'manual' — nearest, with a 4-tap bilinear in the ABI. ⚠️ Linear on a 32F texture without the extension samples (0, 0, 0, 1) everywhere, with no error. Read once from the live renderer; 'manual' (safe anywhere) until the stage is up, uncached.
let fieldFilter = null
export const fieldFilterOf = () => {
	if (fieldFilter) return fieldFilter
	const context = globalThis.pixi_app?.renderer?.context
	if (!context) return 'manual'
	fieldFilter = context.webGLVersion === 2 && context.extensions?.floatTextureLinear ? 'linear' : 'manual'
	return fieldFilter
}

/** The filter the stage chose, or null before anything asked with the stage up. */
export const chosenFieldFilter = () => fieldFilter

// ⚠️ `alphaMode` is REQUIRED: Pixi's default premultiplies a Float32Array on upload, and a field's a = 0 zeroes every channel (U4a).
export const fieldSource = (field, filter) =>
	new BufferImageSource({
		resource: field.data,
		width: field.width,
		height: field.height,
		format: 'rgba32float',
		alphaMode: 'no-premultiply-alpha',
		scaleMode: filter === 'linear' ? 'linear' : 'nearest',
		addressMode: 'clamp-to-edge',
		autoGenerateMipmaps: false,
	})

/** `filter|key` → { source, users (nodes holding it), pinned (in the warm set) }. */
const resident = new Map()
const idOf = (filter, key) => `${filter}|${key}`
const live = entry => entry && !entry.source.destroyed

/** Acquire a warmed or resident field texture for a transition endpoint. */
export const acquireFieldTexture = (key, field, filter) => {
	const id = idOf(filter, key)
	const entry = resident.get(id)
	if (live(entry)) {
		entry.users++
		return entry.source
	}
	const source = fieldSource(field, filter)
	resident.set(id, { source, users: 1, pinned: false })
	return source
}

/** Release one endpoint reference acquired with `acquireFieldTexture`. */
export const releaseFieldTexture = (key, source, filter) => {
	const id = idOf(filter, key)
	const entry = resident.get(id)
	if (!entry || entry.source !== source) {
		if (!source.destroyed) source.destroy()
		return
	}
	entry.users--
	if (entry.users <= 0 && !entry.pinned) drop(id, entry)
}

// Pinned bytes are capped at the CPU cache's budget: a warm set too large to keep CPU-side is not kept GPU-side either.
let pinnedBytes = 0
let warnedBudget = false
const bytesOf = source => source.pixelWidth * source.pixelHeight * 16

const drop = (id, entry) => {
	resident.delete(id)
	if (entry.pinned) pinnedBytes -= bytesOf(entry.source)
	entry.source.destroy()
}

/**
 * Point a node's `held` (field key → source, mutated) at the textures `wanted` (field key → field) needs, under `filter`.
 * A key already resident (another node's, or warmed) is shared. A key this node let go that nothing else holds or pins goes to a new key of the same size, re-uploaded in place (texSubImage2D: a drag walks keys at one size); what is left over is destroyed.
 * `rebind()` runs once `held` is final and BEFORE anything is destroyed: the node points its meshes at the new sources there. ⚠️ Destroying a source a mesh still binds destroys that mesh's whole BindGroup (Pixi's onResourceChange), and its next uniform write throws (U6g FOUND, U4h).
 */
export const bindFieldTextures = (held, wanted, filter, rebind) => {
	const free = []
	for (const [key, source] of held) {
		if (wanted.has(key)) continue
		held.delete(key)
		const id = idOf(filter, key)
		const entry = resident.get(id)
		if (!entry || entry.source !== source) {
			// Not from the resident set (a fake, or adopted before it): this node's alone.
			free.push({ id: null, source })
			continue
		}
		entry.users--
		if (entry.users <= 0 && !entry.pinned) free.push({ id, source })
	}
	for (const [key, field] of wanted) {
		if (held.has(key)) continue
		const id = idOf(filter, key)
		const entry = resident.get(id)
		if (live(entry)) {
			entry.users++
			held.set(key, entry.source)
			continue
		}
		const i = free.findIndex(
			f => !f.source.destroyed && f.source.pixelWidth === field.width && f.source.pixelHeight === field.height
		)
		let source
		if (i >= 0) {
			const reused = free.splice(i, 1)[0]
			if (reused.id) resident.delete(reused.id)
			source = reused.source
			source.resource = field.data
			source.update()
		} else source = fieldSource(field, filter)
		resident.set(id, { source, users: 1, pinned: false })
		held.set(key, source)
	}
	rebind?.()
	for (const { id, source } of free) {
		if (id) resident.delete(id)
		source.destroy()
	}
}

/** A node is gone: let go of everything it held. A texture still held or pinned stays. */
export const releaseFieldTextures = (held, filter) => {
	for (const [key, source] of held) {
		const id = idOf(filter, key)
		const entry = resident.get(id)
		if (!entry || entry.source !== source) {
			source.destroy()
			continue
		}
		if (--entry.users <= 0 && !entry.pinned) drop(id, entry)
	}
	held.clear()
}

/**
 * Pin the texture for a warmed field, creating it if no node has. Returns the source to upload (`renderer.prepare.upload`), or null when it is already on the GPU or the pinned set is at budget.
 */
export const warmFieldTexture = (key, field, filter) => {
	const id = idOf(filter, key)
	const entry = resident.get(id)
	if (live(entry)) {
		if (!entry.pinned) {
			entry.pinned = true
			pinnedBytes += bytesOf(entry.source)
		}
		return null
	}
	const size = field.width * field.height * 16
	if (pinnedBytes + size > FIELD_CACHE_BYTES) {
		if (!warnedBudget) logger.warn('warm set over the texture budget: the rest bakes and uploads on cue')
		warnedBudget = true
		return null
	}
	const source = fieldSource(field, filter)
	resident.set(id, { source, users: 0, pinned: true })
	pinnedBytes += size
	return source
}

/** The warm settled on `keys` (field keys) under `filter`: every other pin goes, and a texture no node holds goes with it. */
export const settleFieldPins = (keys, filter) => {
	const keep = new Set([...keys].map(key => idOf(filter, key)))
	for (const [id, entry] of resident) {
		if (!entry.pinned || keep.has(id)) continue
		entry.pinned = false
		pinnedBytes -= bytesOf(entry.source)
		if (entry.users <= 0) drop(id, entry)
	}
	warnedBudget = false
}

/** For the probe and tests: resident textures, how many are pinned, their bytes. */
export const fieldTextureStats = () => {
	let pinned = 0
	for (const entry of resident.values()) if (entry.pinned) pinned++
	return { resident: resident.size, pinned, pinnedBytes, keys: [...resident.keys()] }
}

/** Tests only: forget the resident set (destroys every texture). */
export const resetFieldTextures = () => {
	for (const entry of resident.values()) entry.source.destroy()
	resident.clear()
	pinnedBytes = 0
	warnedBudget = false
	fieldFilter = null
}
